// frontend/src/pages/SetPassword.jsx
import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, Loader2, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { apiClient } from '../api/client';
import { useLanguageStore } from '../store/useLanguageStore';
import { LanguageSelector } from '../components/LanguageSelector';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
const PASSWORD_ERRORS = {
  'MIN_8_CHARS': 'set_password.errors.short_pass',
  'REQUIRES_UPPERCASE': 'set_password.errors.requires_upper',
  'REQUIRES_NUMBER': 'set_password.errors.requires_number',
};
// Idiomas válidos para DatiaFlow
const VALID_LANGUAGES = ['es', 'en', 'pt'];

export const SetPassword = () => {
  const { t, language, setLanguage } = useLanguageStore();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [success, setSuccess] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // ── Detectar idioma del query param ?lang=pt ──────────────────────
  // El email ya se envió en el idioma del usuario (sec_user.idLanguage).
  // El link incluye &lang=xx para que la pantalla coincida con el email.
  useEffect(() => {
    const langParam = searchParams.get('lang')?.toLowerCase();
    if (langParam && VALID_LANGUAGES.includes(langParam) && langParam !== language) {
      setLanguage(langParam);
    }
  }, []);  // Solo al montar — no re-ejecutar en cambios de language

  const passwordSchema = useMemo(() => z.object({
    password: z.string().min(8, t('set_password.errors.short_pass') || 'Mínimo 8 caracteres'),
    confirm_password: z.string().min(1, t('common.validation_error') || 'Requerido')
  }).refine(data => data.password === data.confirm_password, {
    message: t('set_password.errors.pass_mismatch') || 'Las contraseñas no coinciden',
    path: ['confirm_password']
  }), [t, language]);

  // CONFIGURACIÓN RHF
  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(passwordSchema)
  });

  // ONSUBMIT (Fail Fast del Token + Petición segura)
  const onSubmit = async (data) => {
    setGlobalError('');

    if (!token) {
      setGlobalError(t('set_password.errors.invalid_link') || 'Enlace inválido o incompleto.');
      return;
    }

    setIsSubmitting(true);

    try {
      await apiClient.post('/auth/reset-password', {
        token: token,
        new_password: data.password
      });

      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);

      // } catch (err) {
      //   setGlobalError(err.message || t('common.error_server'));
      // } finally {
    } catch (err) {
      const raw = err.response?.data?.detail || err.message || '';
      const key = Object.keys(PASSWORD_ERRORS).find(k => raw.includes(k));
      setGlobalError(key ? t(PASSWORD_ERRORS[key]) : (raw || t('common.error_server')));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center relative animate-in zoom-in-95">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-black text-slate-800 mb-2">
            {t('set_password.title_success') || '¡Cuenta Activada!'}
          </h2>
          <p className="text-slate-500">
            {t('set_password.msg_success') || 'Tu contraseña ha sido guardada. Redirigiendo al inicio de sesión...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full relative animate-in fade-in duration-500">

        <div className="absolute top-6 right-6">
          <LanguageSelector />
        </div>

        <div className="flex justify-center mb-6">
          <div className="bg-blue-100 p-3 rounded-full">
            <Lock className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <h2 className="text-2xl font-black text-center text-slate-800 mb-2">
          {t('set_password.title_form') || 'Crea tu Contraseña'}
        </h2>
        <p className="text-center text-slate-500 text-sm mb-6">
          {t('set_password.subtitle_form') || 'Para activar tu cuenta y acceder en DatiaFlow, por favor establece tu contraseña segura.'}
        </p>

        {globalError && (
          <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm text-center font-medium mb-4 animate-in fade-in zoom-in duration-200">
            {globalError}
          </div>
        )}

        {/* FORMULARIO CON RHF Y ZOD (noValidate apagando el navegador) */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>

          <div className="relative">
            <label className="block text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-2">
              {t('set_password.fields.new_pass') || 'Nueva Contraseña'}
            </label>
            <input
              type={showPassword ? "text" : "password"}
              {...register('password')}
              className={`w-full bg-slate-50 border-2 rounded-2xl py-3 px-4 pr-12 outline-none font-medium text-sm transition-all ${errors.password ? 'border-red-300 focus:border-red-500' : 'border-transparent focus:border-blue-100'
                }`}
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-[38px] text-slate-400 hover:text-blue-600 transition-colors"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
            {errors.password && <span className="text-red-500 text-[10px] font-bold ml-2 mt-1 block">{errors.password.message}</span>}
          </div>

          <div className="relative">
            <label className="block text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-2">
              {t('set_password.fields.confirm_pass') || 'Confirmar Contraseña'}
            </label>
            <input
              type={showConfirmPassword ? "text" : "password"}
              {...register('confirm_password')}
              className={`w-full bg-slate-50 border-2 rounded-2xl py-3 px-4 pr-12 outline-none font-medium text-sm transition-all ${errors.confirm_password ? 'border-red-300 focus:border-red-500' : 'border-transparent focus:border-blue-100'
                }`}
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-4 top-[38px] text-slate-400 hover:text-blue-600 transition-colors"
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
            {errors.confirm_password && <span className="text-red-500 text-[10px] font-bold ml-2 mt-1 block">{errors.confirm_password.message}</span>}
          </div>

          <button
            type="submit"
            disabled={isSubmitting || !token}
            className="w-full bg-blue-600 text-white py-4 rounded-2xl font-black mt-6 shadow-xl hover:bg-blue-700 transition-all flex justify-center gap-2 disabled:bg-slate-400 disabled:shadow-none text-sm"
          >
            {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : (t('set_password.buttons.submit') || 'Activar y Guardar')}
          </button>

        </form>
      </div>
    </div>
  );
};